/**
 * Cost rate basis helpers — body style (Lorry, …) vs tonnage class (26T, …).
 * Mirrors zeecore/src/projections/profitability/costRateBasis.ts for the app.
 */

export type CostRateBasis = 'body_style' | 'tonnage';

export type BodyStyleClass =
  | 'Lorry'
  | 'Tractor Unit'
  | 'Mini Truck'
  | 'Service Van'
  | 'Rigid Truck'
  | 'Default';

export type TonnageClass = '3.5T' | '7.5T' | '18T' | '26T' | 'Artic' | 'Default';

export const BODY_STYLE_CLASSES: BodyStyleClass[] = [
  'Lorry',
  'Tractor Unit',
  'Mini Truck',
  'Service Van',
  'Rigid Truck',
  'Default',
];

export const TONNAGE_CLASSES: TonnageClass[] = ['3.5T', '7.5T', '18T', '26T', 'Artic', 'Default'];

export const DEFAULT_COST_RATE_BASIS: CostRateBasis = 'body_style';

export function normaliseBodyStyleClass(raw: string | null | undefined): BodyStyleClass {
  const normalised = (raw || '').trim();
  if (/^lorry$/i.test(normalised)) return 'Lorry';
  if (/^tractor\s*unit$/i.test(normalised)) return 'Tractor Unit';
  if (/^mini\s*truck$/i.test(normalised)) return 'Mini Truck';
  if (/^service\s*van$/i.test(normalised)) return 'Service Van';
  if (/^rigid\s*truck$/i.test(normalised)) return 'Rigid Truck';
  return 'Default';
}

export function normaliseTonnageClass(raw: string | null | undefined): TonnageClass {
  const normalised = (raw || '').trim().toLowerCase().replace(/\s+/g, '').replace(/,/g, '.');
  if (!normalised || normalised === '—' || normalised === '-') return 'Default';

  if (/^(artic|articulated)$/i.test(normalised)) return 'Artic';
  if (/^44(\.0+)?t?$/.test(normalised)) return 'Artic';
  if (/^3\.?5(\.0+)?t?$/.test(normalised)) return '3.5T';
  if (/^7\.?5(\.0+)?t?$/.test(normalised)) return '7.5T';
  if (/^18(\.0+)?t?$/.test(normalised)) return '18T';
  if (/^26(\.0+)?t?$/.test(normalised)) return '26T';

  if (/\b44(\.0+)?\b/.test(normalised) || /artic/.test(normalised)) return 'Artic';
  if (/\b3\.?5\b/.test(normalised)) return '3.5T';
  if (/\b7\.?5\b/.test(normalised)) return '7.5T';
  if (/\b18(\.0+)?\b/.test(normalised)) return '18T';
  if (/\b26(\.0+)?\b/.test(normalised)) return '26T';

  return 'Default';
}

export function resolveCostRateKey(
  rawLabel: string | null | undefined,
  basis: CostRateBasis,
): BodyStyleClass | TonnageClass {
  return basis === 'tonnage'
    ? normaliseTonnageClass(rawLabel)
    : normaliseBodyStyleClass(rawLabel);
}
